import React, { useState } from "react";
import SectionHeader from "./SectionHeader";
import { profile } from "../mock";
import { Mail, Linkedin, Github, MapPin, Send, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import axios from "axios";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing fields", description: "Please complete all fields before sending." });
      return;
    }
    setLoading(true);
    try {
      await axios.post("http://localhost:8000/api/contact", {
        name: form.name,
        email: form.email,
        message: form.message,
      });
      setSent(true);
      toast({
        title: "Transmission Received",
        description: "Your message has been queued. Expect a response within 24h.",
      });
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (error) {
      console.error("Transmission failed:", error);
      toast({
        title: "Transmission Error",
        description: "Could not establish secure channel. Please check backend connection.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const channels = [
    { icon: Mail, label: "EMAIL", value: profile.email, href: `mailto:${profile.email}` },
    {
      icon: Linkedin,
      label: "LINKEDIN",
      value: "linkedin.com/in/avadhut-lohar",
      href: profile.linkedin,
    },
    { icon: Github, label: "GITHUB", value: "github.com/avadhutlohar", href: profile.github },
  ];

  return (
    <section id="contact" className="relative px-6 lg:px-10 py-20 border-t border-[#1c2533]">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          num="08 // SECURE CHANNEL"
          title="CONTACT"
          subtitle="OPEN TO OPPORTUNITIES // RESPONSE < 24H"
        />

        <div className="grid lg:grid-cols-12 gap-4">
          {/* Left channels */}
          <div className="lg:col-span-5 space-y-3">
            {channels.map((c) => {
              const Ico = c.icon;
              return (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.label !== "EMAIL" ? "_blank" : undefined}
                  rel="noreferrer"
                  className="flex items-center gap-4 border border-[#1c2533] bg-[#0c1118] p-5 card-hover group"
                >
                  <div className="w-11 h-11 grid place-items-center border border-[#2a3a52] text-cyan-400 group-hover:border-cyan-400 transition-colors">
                    <Ico size={18} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] tracking-[0.2em] text-slate-500">
                      {c.label}
                    </div>
                    <div className="font-mono text-[13.5px] text-slate-100 truncate group-hover:text-cyan-400 transition-colors">
                      {c.value}
                    </div>
                  </div>
                  <span className="font-mono text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all">
                    →
                  </span>
                </a>
              );
            })}

            <div className="border border-cyan-400/40 bg-cyan-400/5 p-5 corner-brackets">
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={14} className="text-cyan-400" />
                <span className="font-mono text-[10px] tracking-[0.2em] text-cyan-400">
                  PHONE / DIRECT
                </span>
              </div>
              <div className="font-mono text-sm text-slate-100">{profile.phone}</div>
              <div className="text-slate-400 text-[13px] mt-2 leading-relaxed">
                Actively open to <span className="text-cyan-400">L2 / Senior SOC Analyst</span>{" "}
                and Incident Response roles in 24x7 enterprise environments. Available for hybrid
                and remote engagements with flexibility for shift rotations and bridge calls.
              </div>
            </div>
          </div>

          {/* Right secure form */}
          <div className="lg:col-span-7 border border-[#1c2533] bg-[#0c1118] corner-brackets">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#1c2533] bg-[#0a0e14]">
              <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-slate-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
                ENCRYPTED CHANNEL
              </div>
              <span className="font-mono text-[10px] tracking-[0.18em] text-slate-500">
                TLS-1.3 // E2EE
              </span>
            </div>
            <form onSubmit={onSubmit} className="p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-2">
                    IDENTITY
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="YOUR NAME"
                    className="w-full bg-[#0a0e14] border border-[#1c2533] focus:border-cyan-400 outline-none px-4 py-2.5 font-mono text-sm text-slate-100 placeholder:text-slate-600 rounded-sm transition-colors"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-2">
                    RETURN ADDRESS
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="name@domain.com"
                    className="w-full bg-[#0a0e14] border border-[#1c2533] focus:border-cyan-400 outline-none px-4 py-2.5 font-mono text-sm text-slate-100 placeholder:text-slate-600 rounded-sm transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-2">
                  PAYLOAD
                </label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Describe the opportunity, role, or message..."
                  className="w-full bg-[#0a0e14] border border-[#1c2533] focus:border-cyan-400 outline-none px-4 py-3 font-mono text-sm text-slate-100 placeholder:text-slate-600 rounded-sm transition-colors resize-none"
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="font-mono text-[10px] tracking-[0.18em] text-slate-500">
                  STATUS:{" "}
                  {loading ? (
                    <span className="text-cyan-400">TRANSMITTING...</span>
                  ) : sent ? (
                    <span className="text-emerald-400 font-bold">TRANSMITTED</span>
                  ) : (
                    <span className="text-emerald-400">READY TO TRANSMIT</span>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading || sent}
                  className="group inline-flex items-center gap-2.5 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-60 text-slate-900 font-mono font-semibold text-xs tracking-[0.18em] px-5 py-3 rounded-sm transition-colors"
                >
                  {loading ? (
                    <>TRANSMITTING...</>
                  ) : sent ? (
                    <>
                      <CheckCircle2 size={14} /> TRANSMITTED
                    </>
                  ) : (
                    <>
                      <Send size={14} className="group-hover:translate-x-0.5 transition-transform" />{" "}
                      SEND TRANSMISSION
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
